import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, filter, of, switchMap, take, map, catchError } from 'rxjs';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { AuthService } from '../auth/auth.service';
import { isAuthDeadError } from '../auth/auth-errors';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  constructor(
    private readonly auth0: Auth0Service,
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    // On native (Android/iOS) the Auth0 Angular SDK is not used for authentication,
    // so waiting for isLoading$ would cause a deadlock. Skip it on native.
    // (validateSession=false: el token nativo lo gestiona Auth0.swift/.Android.)
    if (Capacitor.isNativePlatform()) {
      return this.checkAuth(state.url, false);
    }

    // On web: wait for Auth0 SDK to finish loading before checking auth state.
    // Using take(1) directly on isAuthenticated$ can grab the initial false
    // emission while the SDK is still resolving the session.
    return this.auth0.isLoading$.pipe(
      filter(loading => !loading),
      take(1),
      switchMap(() => this.checkAuth(state.url, true))
    );
  }

  private checkAuth(url: string, validateSession: boolean): Observable<boolean> {
    return this.authService.isAuthenticated$.pipe(
      take(1),
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return of(this.redirectToLogin(url));
        }
        if (!validateSession) {
          return of(true);
        }
        // isAuthenticated venía de la caché de localStorage: con la sesión
        // MUERTA (refresh token consumido/caducado) sigue diciendo true, y la
        // app entraba sin datos hasta que el session-manager expulsaba al
        // usuario a los segundos (app#558). La única verdad es poder emitir un
        // token: si hay uno cacheado y válido, getAccessTokenSilently resuelve
        // al instante sin red; si la sesión está muerta, lanza un código de
        // AUTH_DEAD_ERRORS y se redirige a login LIMPIAMENTE antes de renderizar.
        return this.auth0.getAccessTokenSilently().pipe(
          map(() => {
            // ── EL TOKEN TIENE QUE TRAER CLIENTE ──────────────────────────────────
            //
            // Estar autenticado no basta: con una sola URL para varios clientes, un
            // token sin tenant no identifica a NADIE. El borde lo rechaza con 403, pero
            // el frontend ya habría renderizado la aplicación entera — y lo que ve el
            // usuario es una app cargada con todos los paneles vacíos, que se parece
            // mucho más a "no tengo datos" que a "no se sabe quién eres".
            //
            // `currentTenantId` se restaura de localStorage de forma síncrona al arrancar
            // y el callback espera a `estadoTenant$` antes de navegar, así que aquí ya
            // está resuelto: no hay carrera con la decodificación del token.
            if (!this.authService.currentTenantId) {
              console.error(
                '[AuthGuard] el token no trae cliente: el usuario no es miembro de ninguna ' +
                'organización, o la Action Post-Login no está emitiendo el claim.',
              );
              return this.denyWithoutTenant();
            }
            return true;
          }),
          catchError((err: unknown) => {
            if (isAuthDeadError(err)) {
              const code = (err as { error?: string })?.error ?? '';
              console.warn(`[AuthGuard] sesión sin capacidad de emitir token (${code}) → recuperación`);
              // Recuperación limpia conservando la URL destino: limpia la
              // caché zombie (isAuthenticated$ → false, adiós al ping-pong
              // /login↔ruta del intended_url) y re-loguea sin destruir la
              // SSO — si sigue viva, el usuario vuelve sin credenciales.
              this.authService.handleSessionDead(url);
              return of(false);
            }
            // Fallo transitorio (red, timeout): dejar pasar — la capa de
            // datos mostrará sus propios errores.
            return of(true);
          })
        );
      })
    );
  }

  private redirectToLogin(url: string): boolean {
    localStorage.setItem('intended_url', url);
    this.router.navigate(['/login']);
    return false;
  }

  /**
   * Sesión válida pero SIN cliente: se corta aquí, y a propósito NO como `redirectToLogin`.
   *
   * Aquella guarda `intended_url` para volver tras el login; pero este usuario YA está
   * autenticado, y `/login` devuelve a un autenticado a su `intended_url`… que este guard
   * vuelve a denegar. Es un bucle /login ↔ ruta que la versión de stage (3c1dc37) dejaba
   * latente. Sin `intended_url`, el usuario se queda en /login con el botón de entrar, que
   * es desde donde puede autenticarse contra una organización de la que sí sea miembro.
   */
  private denyWithoutTenant(): boolean {
    localStorage.removeItem('intended_url');
    this.router.navigate(['/login']);
    return false;
  }
}
